import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { PLATFORM_META } from "../lib/api";
import { authedRedirectUrl } from "../lib/auth";
import { Plus, Trash2, Loader2, Link2, CheckCircle2, Users, AlertTriangle, Settings2, Copy, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const inputCls = "w-full bg-slate-950/60 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/60 transition-colors";

export const AccountsView = () => {
  const [accounts, setAccounts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [savingKeys, setSavingKeys] = useState(false);
  const [form, setForm] = useState({ platform: "linkedin", display_name: "", handle: "", brand_id: "", follower_count: 5000 });

  const load = async () => {
    setLoading(true);
    const [a, b, c, st] = await Promise.all([api.get("/accounts"), api.get("/brands"), api.get("/oauth/config"), api.get("/settings")]);
    setAccounts(a.data); setBrands(b.data); setCfg(c.data); setSettings(st.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));
  const setKey = (k, v) => setSettings((c) => ({ ...c, [k]: v }));

  const saveKeys = async () => {
    setSavingKeys(true);
    try {
      const p = { ...settings }; delete p.id;
      await api.put("/settings", p);
      toast.success("Developer keys saved");
      await load();
    } catch { toast.error("Save failed"); } finally { setSavingKeys(false); }
  };

  const connectSim = async () => {
    if (!form.display_name.trim()) return toast.error("Account name required");
    setConnecting(true);
    try {
      await api.post("/accounts", { platform: form.platform, display_name: form.display_name, handle: form.handle,
        brand_id: form.brand_id || null, follower_count: Number(form.follower_count) || 5000 });
      toast.success(`Added demo ${PLATFORM_META[form.platform].label} account`);
      setOpen(false); setForm({ platform: "linkedin", display_name: "", handle: "", brand_id: "", follower_count: 5000 });
      await load();
    } catch { toast.error("Failed"); } finally { setConnecting(false); }
  };

  const disconnect = async (id) => { await api.delete(`/accounts/${id}`); toast("Account removed"); await load(); };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const brandName = (id) => brands.find((b) => b.id === id)?.name;
  const grouped = Object.keys(PLATFORM_META).map((p) => ({ platform: p, list: accounts.filter((a) => a.platform === p) }));

  return (
    <div className="space-y-6" data-testid="accounts-view">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold tracking-wider uppercase text-indigo-400 mb-2">Connections</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-50">Accounts</h1>
          <p className="text-slate-400 text-sm mt-2">Connect LinkedIn profiles and Pages, Facebook, Instagram and Threads - or add demo accounts for testing.</p>
        </div>
        <button data-testid="dev-setup-toggle" onClick={() => setShowSetup((v) => !v)}
          className="flex items-center gap-2 px-4 py-2 rounded-full glass text-slate-300 hover:text-white text-sm font-semibold transition-colors">
          <Settings2 className="w-4 h-4" /> Developer setup <ChevronDown className={`w-4 h-4 transition-transform ${showSetup ? "rotate-180" : ""}`} />
        </button>
      </div>

      {/* One run through every network's consent screen */}
      {cfg?.connect_all_steps?.length > 1 && (
        <div className="rounded-2xl p-5 flex flex-wrap items-center gap-4 border border-indigo-500/30 bg-gradient-to-r from-indigo-500/10 to-fuchsia-500/10">
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm font-semibold text-slate-100">Connect everything in one go</p>
            <p className="text-xs text-slate-400 mt-0.5">
              {cfg.connect_all_steps.map((p) => ({ linkedin: cfg.linkedin_includes_pages ? "LinkedIn profile + pages" : "LinkedIn",
                linkedin_pages: "LinkedIn Pages", meta: "Facebook & Instagram", threads: "Threads" }[p])).join(" → ")}
              {" "}- one "Allow" on each screen, then you're back here.
            </p>
          </div>
          <button data-testid="connect-all-btn"
            onClick={() => { window.location.href = authedRedirectUrl("/auth/connect-all/start"); }}
            className="px-5 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold transition-colors">
            Connect all
          </button>
        </div>
      )}

      {/* Real connect buttons */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="glass rounded-2xl p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-heading font-bold" style={{ background: PLATFORM_META.linkedin.color }}>in</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-100">LinkedIn</p>
            <p className="text-xs text-slate-500">{!cfg?.linkedin_configured ? "Add developer keys first"
              : cfg.linkedin_includes_pages ? "Your profile and every page you admin, in one sign-in" : "Ready — sign in to connect"}</p>
          </div>
          <button data-testid="connect-linkedin-btn" disabled={!cfg?.linkedin_configured}
            onClick={() => { window.location.href = authedRedirectUrl("/auth/linkedin/start"); }}
            className="px-4 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Connect</button>
        </div>
        {cfg?.linkedin_pages_separate && (
        <div className="glass rounded-2xl p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-heading font-bold relative" style={{ background: PLATFORM_META.linkedin.color }}>
            in<span className="absolute -bottom-1 -right-1 text-[9px] px-1 rounded bg-slate-900 text-slate-200 border border-slate-700">Page</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-100">LinkedIn Pages</p>
            <p className="text-xs text-slate-500">{cfg?.linkedin_pages_configured
              ? "Post as a company page - every page you admin is added" : "Add developer keys first"}</p>
          </div>
          <button data-testid="connect-linkedin-pages-btn" disabled={!cfg?.linkedin_pages_configured}
            onClick={() => { window.location.href = authedRedirectUrl("/auth/linkedin-pages/start"); }}
            className="px-4 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Connect</button>
        </div>
        )}
        <div className="glass rounded-2xl p-5 flex items-center gap-4">
          <div className="flex -space-x-2">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-heading font-bold ring-2 ring-slate-900" style={{ background: PLATFORM_META.facebook.color }}>f</div>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-heading font-bold ring-2 ring-slate-900" style={{ background: PLATFORM_META.instagram.color }}>ig</div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-100">Facebook & Instagram</p>
            <p className="text-xs text-slate-500">{cfg?.meta_configured ? "Ready — connects Pages + linked IG" : "Add developer keys first"}</p>
          </div>
          <button data-testid="connect-meta-btn" disabled={!cfg?.meta_configured}
            onClick={() => { window.location.href = authedRedirectUrl("/auth/meta/start"); }}
            className="px-4 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Connect</button>
        </div>
        <div className="glass rounded-2xl p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white text-xl font-bold bg-black border border-slate-700">@</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-100">Threads</p>
            <p className="text-xs text-slate-500">{cfg?.threads_configured ? "Ready — sign in with the Threads profile" : "Add developer keys first"}</p>
          </div>
          <button data-testid="connect-threads-btn" disabled={!cfg?.threads_configured}
            onClick={() => { window.location.href = authedRedirectUrl("/auth/threads/start"); }}
            className="px-4 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Connect</button>
        </div>
      </div>

      {/* Developer setup panel */}
      {showSetup && settings && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="glass rounded-2xl p-6 space-y-5" data-testid="dev-setup-panel">
          <div>
            <p className="text-sm font-heading font-bold text-slate-100 mb-1">Paste these callback URLs into your developer apps</p>
            <div className="space-y-2 mt-3">
              {[["LinkedIn redirect URL", cfg?.linkedin_redirect_uri], ["LinkedIn Pages redirect URL", cfg?.linkedin_pages_redirect_uri],
                ["Meta redirect URL", cfg?.meta_redirect_uri], ["Meta deauthorize callback", cfg?.meta_deauthorize_uri],
                ["Meta data deletion callback", cfg?.meta_delete_uri], ["Threads redirect URL", cfg?.threads_redirect_uri],
                ["Threads uninstall callback", cfg?.threads_uninstall_uri], ["Threads delete callback", cfg?.threads_delete_uri],
              ].filter(([, u]) => u).map(([l, u]) => (
                <div key={l} className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-48 shrink-0">{l}</span>
                  <code className="flex-1 text-[11px] font-mono text-indigo-300 bg-slate-950/60 border border-slate-800 rounded-lg px-3 py-1.5 truncate">{u}</code>
                  <button onClick={() => copy(u)} className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300"><Copy className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-slate-800">
            <div><label className="text-xs text-slate-400 mb-1.5 block">LinkedIn Client ID</label>
              <input data-testid="linkedin-client-id" value={settings.linkedin_client_id || ""} onChange={(e) => setKey("linkedin_client_id", e.target.value)} className={inputCls} /></div>
            <div><label className="text-xs text-slate-400 mb-1.5 block">LinkedIn Client Secret</label>
              <input data-testid="linkedin-client-secret" type="password" value={settings.linkedin_client_secret || ""} onChange={(e) => setKey("linkedin_client_secret", e.target.value)} className={inputCls} /></div>
            <div><label className="text-xs text-slate-400 mb-1.5 block">Meta App ID</label>
              <input data-testid="meta-app-id" value={settings.meta_app_id || ""} onChange={(e) => setKey("meta_app_id", e.target.value)} className={inputCls} /></div>
            <div><label className="text-xs text-slate-400 mb-1.5 block">Meta App Secret</label>
              <input data-testid="meta-app-secret" type="password" value={settings.meta_app_secret || ""} onChange={(e) => setKey("meta_app_secret", e.target.value)} className={inputCls} /></div>
            <div><label className="text-xs text-slate-400 mb-1.5 block">Threads App ID</label>
              <input data-testid="threads-app-id" value={settings.threads_app_id || ""} onChange={(e) => setKey("threads_app_id", e.target.value)} className={inputCls} /></div>
            <div><label className="text-xs text-slate-400 mb-1.5 block">Threads App Secret</label>
              <input data-testid="threads-app-secret" type="password" value={settings.threads_app_secret || ""} onChange={(e) => setKey("threads_app_secret", e.target.value)} className={inputCls} /></div>
            <div><label className="text-xs text-slate-400 mb-1.5 block">LinkedIn Pages Client ID <span className="text-slate-600">(optional)</span></label>
              <input value={settings.linkedin_pages_client_id || ""} onChange={(e) => setKey("linkedin_pages_client_id", e.target.value)} className={inputCls} /></div>
            <div><label className="text-xs text-slate-400 mb-1.5 block">LinkedIn Pages Client Secret <span className="text-slate-600">(optional)</span></label>
              <input type="password" value={settings.linkedin_pages_client_secret || ""} onChange={(e) => setKey("linkedin_pages_client_secret", e.target.value)} className={inputCls} /></div>
            <label className="sm:col-span-2 flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={!!settings.linkedin_includes_pages}
                onChange={(e) => setKey("linkedin_includes_pages", e.target.checked)} className="accent-indigo-500 mt-0.5" />
              <span className="text-xs text-slate-300">
                My LinkedIn app has the <strong>Community Management API</strong> - connect the profile and every page in one sign-in
                <span className="block text-[11px] text-slate-500">Then leave the LinkedIn Pages fields blank.</span>
              </span>
            </label>
            <p className="sm:col-span-2 text-[11px] text-slate-500">
              Threads uses its own app ID and secret: in your Meta app, add the "Access the Threads API" use case.
              LinkedIn Pages need the Community Management API, which LinkedIn grants on its own developer app -
              put that app's keys here, or leave them blank to use the LinkedIn app above.
            </p>
          </div>
          <button data-testid="save-keys-btn" onClick={saveKeys} disabled={savingKeys}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors disabled:opacity-60">
            {savingKeys && <Loader2 className="w-4 h-4 animate-spin" />} Save keys
          </button>
        </motion.div>
      )}

      {/* Account list */}
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-slate-200">Connected accounts</h3>
        <button data-testid="add-demo-account-btn" onClick={() => setOpen(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-full glass text-slate-300 hover:text-white text-sm font-semibold transition-colors">
          <Plus className="w-4 h-4" /> Add demo account
        </button>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div> : (
        accounts.length === 0 ? (
          <div className="glass rounded-2xl p-12 flex flex-col items-center gap-3 text-slate-500">
            <Users className="w-10 h-10" /><p className="text-sm">No accounts yet. Connect a real account above or add a demo account.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.filter((g) => g.list.length).map((g) => (
              <div key={g.platform}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: PLATFORM_META[g.platform].color }} />
                  <h3 className="font-heading font-bold text-slate-200">{PLATFORM_META[g.platform].label}</h3>
                  <span className="text-xs font-mono text-slate-500">({g.list.length})</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {g.list.map((a) => {
                    const reconnect = a.status === "reconnect_needed";
                    const real = a.connection_type === "real";
                    return (
                      <motion.div key={a.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="glass rounded-2xl p-4 flex items-center gap-3" data-testid="account-card">
                        <img src={a.avatar_url || `https://api.dicebear.com/7.x/shapes/svg?seed=${a.handle}`} alt="" className="w-11 h-11 rounded-full bg-slate-800 object-cover" style={{ border: `2px solid ${PLATFORM_META[a.platform].color}` }} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-100 truncate flex items-center gap-1">
                            {a.display_name}
                            {reconnect ? <AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                          </p>
                          <p className="text-xs text-slate-500 truncate">@{a.handle || "handle"}{a.follower_count ? ` · ${(a.follower_count / 1000).toFixed(1)}k` : ""}</p>
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${real ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-600/20 text-slate-400"}`}>{real ? "Real" : "Demo"}</span>
                            {a.platform === "linkedin" && real && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300">{a.account_kind === "page" ? "Page" : "Profile"}</span>
                            )}
                            {reconnect && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">Reconnect needed</span>}
                            {a.brand_id && brandName(a.brand_id) && <span className="text-[10px] text-indigo-300">{brandName(a.brand_id)}</span>}
                          </div>
                        </div>
                        <button data-testid="disconnect-account-btn" onClick={() => disconnect(a.id)} className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-rose-500/20 hover:text-rose-300 flex items-center justify-center text-slate-400 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Demo account dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Link2 className="w-4 h-4 text-indigo-300" /> Add demo account</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Platform</label>
              <div className="flex gap-2">
                {Object.entries(PLATFORM_META).map(([k, m]) => (
                  <button key={k} data-testid={`connect-platform-${k}`} onClick={() => set("platform", k)}
                    className={`flex-1 py-2 rounded-xl text-xs font-semibold border transition-colors ${form.platform === k ? "text-white" : "border-slate-700 text-slate-400"}`}
                    style={form.platform === k ? { background: `${m.color}22`, borderColor: m.color, color: "#fff" } : {}}>{m.label}</button>
                ))}
              </div>
            </div>
            <input data-testid="account-name-input" value={form.display_name} onChange={(e) => set("display_name", e.target.value)} placeholder="Account / page name" className={inputCls} />
            <input data-testid="account-handle-input" value={form.handle} onChange={(e) => set("handle", e.target.value)} placeholder="@handle" className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-400 mb-1.5 block">Followers</label>
                <input data-testid="account-followers-input" type="number" value={form.follower_count} onChange={(e) => set("follower_count", e.target.value)} className={inputCls} /></div>
              <div><label className="text-xs text-slate-400 mb-1.5 block">Brand</label>
                <select data-testid="account-brand-select" value={form.brand_id} onChange={(e) => set("brand_id", e.target.value)} className={inputCls}>
                  <option value="">None</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select></div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-200">Cancel</button>
            <button data-testid="confirm-connect-btn" onClick={connectSim} disabled={connecting} className="px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold flex items-center gap-2">
              {connecting && <Loader2 className="w-4 h-4 animate-spin" />} Add account
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
